import type { FavoriteLineDto } from '../../api/approval';
import type { GroupedSelectGroup, GroupedSelectOption } from '../../components/shared/GroupedSelect';
import type { ApprovalLineValue } from './ApprovalLineBlock';

/**
 * 즐겨찾는 결재선 — 목록 · 요약 · 배지 (2026-09-15 · 대시보드 시안 v2 V9 · 순수 함수).
 *
 * 근거: web-plan §4 규칙 D6 · D7 · D8 · 사용자 결정 A-Q2(「기본」 표시는 **회사 공용 기본만**) · 관제 결정 Q-7.
 *
 * ★★★ **역할은 자리로 센다** — 마지막 한 사람이 「최종결재자」, 그 앞이 「결재자」(서버 `slotOf` · `approval-line-slot.ts` 와 같은 규칙).
 *     즐겨찾기 응답에는 역할 · 직위 칸이 없다(DR4) — 직위가 오기 전까지는 이름만 쓴다.
 * ★★ **회사를 떠난 사람(이름 없음)은 요약 · 채우기에서 뺀다** — 담아 봐야 기안이 막힌다(종전 불러오기와 같다).
 * ★ 「기본 · 휴가」 배지는 `shared` 이면서 `defaultForTypes` 에 **지금 문서 종류**가 있을 때만(A-Q2).
 * ★ 같은 이름 판정은 **같은 묶음 안**에서만 — 서버가 공용 / 내 것을 따로 덮어쓴다(`favorite-line.routes.ts:206-216`).
 */

/** 배지에 쓰는 짧은 문서 종류 이름. 모르는 코드는 코드 그대로. */
export const DOC_TYPE_SHORT: Readonly<Record<string, string>> = {
  LEAVE: '휴가',
  BUSINESS_TRIP: '출장',
  OUTSIDE_WORK: '외근',
};

type Person = { name: string | null };

/** 「박서준(결재자) → 이지훈(최종결재자) · 참고자 오지민 · 회사를 떠난 1명은 빠집니다」. */
export function lineSummary(
  approvers: readonly Person[], referrers: readonly Person[], missing = 0,
): string {
  const named = approvers.map((a) => a.name).filter((n): n is string => Boolean(n));
  const parts: string[] = [
    named.length === 0
      ? '결재자 없음'
      : named.map((n, i) => `${n}(${i === named.length - 1 ? '최종결재자' : '결재자'})`).join(' → '),
  ];
  const refs = referrers.map((r) => r.name).filter((n): n is string => Boolean(n));
  if (refs.length > 0) parts.push(`참고자 ${refs.join(', ')}`);
  if (missing > 0) parts.push(`회사를 떠난 ${missing}명은 빠집니다`);
  return parts.join(' · ');
}

export function isDefaultFor(f: FavoriteLineDto, docType: string): boolean {
  return f.shared === true && (f.defaultForTypes ?? []).includes(docType);
}

/** 트리거 배지 — 회사 기본 / 내 결재선 / (공용이지만 기본이 아니면) 없음. */
export function favoriteBadge(f: FavoriteLineDto, docType: string): '회사 기본' | '내 결재선' | null {
  if (isDefaultFor(f, docType)) return '회사 기본';
  if (f.shared !== true) return '내 결재선';
  return null;
}

/** 묶음 둘 — 회사 공용(이 종류의 기본이 맨 앞) · 내 결재선. 빈 묶음은 뺀다. */
export function favoriteGroups(favorites: readonly FavoriteLineDto[], docType: string): GroupedSelectGroup[] {
  const toOption = (f: FavoriteLineDto): GroupedSelectOption => {
    const tags: string[] = [];
    if (isDefaultFor(f, docType)) tags.push(`기본 · ${DOC_TYPE_SHORT[docType] ?? docType}`);
    if (f.missing > 0) tags.push(`빠진 사람 ${f.missing}`);
    return {
      value: f.id,
      label: f.name,
      ...(tags.length > 0 ? { tag: tags.join(' · ') } : {}),
      detail: lineSummary(f.approvers, f.referrers, f.missing),
      // ★ 지울 수 있는가는 서버가 준다(`canDelete`) — 옛 서버는 안 줘 종전처럼 보인다.
      removable: f.canDelete !== false,
    };
  };
  const shared = favorites.filter((f) => f.shared === true)
    .slice()
    .sort((a, b) => Number(isDefaultFor(b, docType)) - Number(isDefaultFor(a, docType)));
  const mine = favorites.filter((f) => f.shared !== true);
  return [
    { key: 'shared', label: '회사 공용', options: shared.map(toOption) },
    { key: 'mine', label: '내 결재선', options: mine.map(toOption) },
  ].filter((g) => g.options.length > 0);
}

/** 같은 묶음(공용 / 내 것)에 같은 이름이 있나 — 앞뒤 빈칸은 무시. */
export function findSameName(
  favorites: readonly FavoriteLineDto[], name: string, shared: boolean,
): FavoriteLineDto | null {
  const n = name.trim();
  if (n === '') return null;
  return favorites.find((f) => (f.shared === true) === shared && f.name.trim() === n) ?? null;
}

/** 즐겨찾기로 결재자 · 참고자를 채운다. 이름 없는 사람(회사를 떠난)은 뺀다 · 합의는 건드리지 않는다. */
export function favoriteToLine(f: FavoriteLineDto, base: ApprovalLineValue): ApprovalLineValue {
  return {
    ...base,
    approvers: f.approvers.filter((a) => a.name).map((a) => ({ userId: a.userId, name: a.name as string, position: null })),
    referrers: f.referrers.filter((r) => r.name).map((r) => ({ userId: r.userId, name: r.name as string, position: null })),
  };
}
